import type { IAdGroup } from "../../../../../../../../../shared/types/form.type";

export const CAMPAIGN_NAME_MAX = 255;
export const HEADLINE_MAX = 30;
export const DESCRIPTION_MAX = 90;
const ALLOWED_CHARS_REGEX = /^[a-zA-Z0-9 _\-.,!?():]+$/;
// Тексти оголошень: літери будь-якої мови з діакритикою, цифри, пунктуація, лапки/апострофи/тире,
// & % + @ # / ( ) ™ ®, символи валют, а також звичайна пунктуація інших мов (… ¡ ¿ №, CJK, арабська,
// іврит, деванагарі, повноширинні ％ ＆ ／ ＃ － ．, ～, 【】) і нерозривні пробіли. Не дозволені: емодзі, селектори варіантів (U+FE0E/FE0F),
// охоплюючі знаки та декоративні символи, а також однобайтна катакана (прямо заборонена Google),
// невидимі заповнювачі хангиля і «шрифтові» математичні літери (U+1D400–1D7FF).
const AD_TEXT_ALLOWED_CHARS_REGEX =
	/^(?!.*[\uFE0E\uFE0F\u115F\u1160\u3164\uFFA0\uFF66-\uFF9F\u{1D400}-\u{1D7FF}])[\p{L}\p{Mn}\p{Mc}\p{Nd} _.,!?:;'’‘"“”«»„()\-–—/&%+@#\u2026\u00A1\u00BF\u2116\u3002\u3001\uFF0C\uFF01\uFF1F\uFF1A\uFF1B\uFF08\uFF09\u300C\u300D\u300E\u300F\u300A\u300B\u30FB\u301C\u060C\u061B\u061F\u05F3\u05F4\u05BE\u0964\u0965\uFF3F\uFF0E\uFF07\uFF02\uFF0D\uFF0F\uFF06\uFF05\uFF0B\uFF20\uFF03\uFF5E\u3010\u3011\u3008\u3009\u3014\u3015\u066A\u06D4\u00B7\u2011\u00A0\u202F\u3000\p{Sc}™®]+$/u;

const REPEATED_PUNCTUATION_REGEX =
	/([^\p{L}\p{Mn}\p{Mc}\p{Nd}\s])\1|[!?\uFF01\uFF1F\u061F]{2}/u;

const EXCLAMATION_MARKS = "!\uFF01";
const QUESTION_MARKS = "?\uFF1F\u061F";

export type TextFieldValidationIssue =
	| "empty"
	| "too_long"
	| "invalid_chars"
	| "repeated_punctuation"
	| "headline_question_mark"
	| "headline_exclamation"
	| "too_many_exclamations"
	| "question_mark_position";

function validateTrimmedText(
	raw: string,
	maxLen: number,
	allowedChars: RegExp,
): TextFieldValidationIssue | null {
	const s = raw.trim();
	if (!s) return "empty";
	if (s.length > maxLen) return "too_long";
	if (!allowedChars.test(s)) return "invalid_chars";
	return null;
}

function validateAdText(
	raw: string,
	maxLen: number,
	kind: "headline" | "description",
): TextFieldValidationIssue | null {
	const issue = validateTrimmedText(raw, maxLen, AD_TEXT_ALLOWED_CHARS_REGEX);
	if (issue) return issue;
	const s = raw.trim();
	if (REPEATED_PUNCTUATION_REGEX.test(s)) return "repeated_punctuation";
	const chars = Array.from(s);
	const exclamations = chars.filter((ch) => EXCLAMATION_MARKS.includes(ch)).length;
	const questions = chars.filter((ch) => QUESTION_MARKS.includes(ch)).length;
	if (kind === "headline") {
		if (questions > 0) return "headline_question_mark";
		if (exclamations > 1 || EXCLAMATION_MARKS.includes(chars[0])) {
			return "headline_exclamation";
		}
		return null;
	}
	if (exclamations > 1) return "too_many_exclamations";
	if (
		questions > 1 ||
		(questions === 1 && !QUESTION_MARKS.includes(chars[chars.length - 1]))
	) {
		return "question_mark_position";
	}
	return null;
}

// Назва кампанії: не порожня після trim, ≤255 символів, дозволений набір символів.
export function getCampaignNameValidationIssue(
	raw: string,
): TextFieldValidationIssue | null {
	return validateTrimmedText(raw, CAMPAIGN_NAME_MAX, ALLOWED_CHARS_REGEX);
}

export function isCampaignNameTextValid(raw: string): boolean {
	return getCampaignNameValidationIssue(raw) === null;
}

// Один заголовок оголошення: не порожній після trim, ≤30 символів, дозволений набір,
// без повторів пунктуації, без «?», «!» не більше одного і не першим.
export function getHeadlineValidationIssue(
	raw: string,
): TextFieldValidationIssue | null {
	return validateAdText(raw, HEADLINE_MAX, "headline");
}

// Проверяет поле на null
export const isHeadlineTextValid = (raw: string): boolean => {
	return getHeadlineValidationIssue(raw) === null;
};

// Один опис оголошення: не порожній після trim, ≤90 символів, дозволений набір,
// без повторів пунктуації, «!» не більше одного, «?» не більше одного і лише останнім символом.
export function getDescriptionValidationIssue(
	raw: string,
): TextFieldValidationIssue | null {
	return validateAdText(raw, DESCRIPTION_MAX, "description");
}

export function isDescriptionTextValid(raw: string): boolean {
	return getDescriptionValidationIssue(raw) === null;
}

// Проверка конкретного названия на уникальность без учета регистра
export const hasDuplicateNonEmptyAdGroupNamesIgnoreCase = (
	adgroups: IAdGroup[],
): boolean => {
	return (
		adgroups.map((ag) => (ag.name ?? "").trim().toLowerCase()).filter(Boolean)
			.length !==
		new Set(adgroups.map((ag) => (ag.name ?? "").trim().toLowerCase())).size
	);
};

// Проверка конкретного названия на дубликат без учета регистра
export const getAdGroupNameDuplicateMessageAtIndex = (
	adgroups: IAdGroup[],
	index: number,
): string | undefined => {
	if (!hasDuplicateNonEmptyAdGroupNamesIgnoreCase(adgroups)) return undefined;
	return adgroups[index]?.name ?? "";
};

/*
	Мінімум одна група. У кожної групи — перший app ad: є ≥1 заголовок, жоден не порожній
	після trim і не порушує правил; є ≥1 опис, усі описи не порожні та валідні;
	непусті назви груп унікальні без урахування регістру.
*/
export const areCampaignAdGroupTextsValid = (adgroups: IAdGroup[]): boolean => {
	if (adgroups.length < 1) return false;
	if (hasDuplicateNonEmptyAdGroupNamesIgnoreCase(adgroups)) return false;

	for (const ag of adgroups) {
		const appad = ag.appads?.[0];
		if (!appad) return false;

		const headlines = appad.headlines ?? [];
		if (headlines.length < 1) return false;
		for (const h of headlines) {
			if (!isHeadlineTextValid(h)) return false;
		}

		const descriptions = appad.descriptions ?? [];
		if (descriptions.length < 1) return false;
		for (const d of descriptions) {
			if (!isDescriptionTextValid(d)) return false;
		}
	}
	return true;
};
